import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { GET_BACKEND_CONFIG, GET_STORE_CONFIG } from '../commonQueries';
import { readBackendConfig } from '../utils/readBackendConfig';
import defaultConfig from '../config/default.json';

const ConfigContext = createContext();

export const ConfigProvider = ({ children }) => {
    const { data: backendConfig, loading: backendConfigLoading, error: backendConfigError } = useQuery(GET_BACKEND_CONFIG);
    const { data: storeConfig, loading: storeConfigLoading, error: storeConfigError } = useQuery(GET_STORE_CONFIG);

    const { config: _omit, ...defaultConfigWithoutNestedConfig } = defaultConfig;

    const _defaultConfig = {
        ...defaultConfig.config,                // zawartość defaultConfig.config
        ...defaultConfigWithoutNestedConfig     // defaultConfig bez pola `config`
    };
    const [mergedConfig, setMergedConfig] = useState(_defaultConfig);
    const isLoading = backendConfigLoading || storeConfigLoading;

    useEffect(() => {
        if (!isLoading && (backendConfig || storeConfig)) {
            const backendConfigData = readBackendConfig(backendConfig, false);
            const storeConfigData = storeConfig?.storeConfig || {};

            const newConfig = {
                ...defaultConfig,
                ...backendConfigData,
                ...storeConfigData,
                ...defaultConfig.config
            };

            setMergedConfig(newConfig);
        }
    }, [backendConfig, storeConfig, isLoading]);

    if (backendConfigError) {
        console.error('Error loading backend config:', backendConfigError);
    }

    if (storeConfigError) {
        console.error('Error loading store config:', storeConfigError);
    }

    if (backendConfigError && storeConfigError) {
        return null;
    }

    return (
        <ConfigContext.Provider value={{ config: mergedConfig, loading: isLoading }}>
            {children}
        </ConfigContext.Provider>
    );
};

ConfigProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export const useConfig = () => {
    const context = useContext(ConfigContext);
    if (!context) {
        throw new Error('useConfig must be used within a ConfigProvider');
    }
    return context;
};
